import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Platform,
} from 'react-native';
import { colors, typography } from '../../theme';
import { useResponsive } from '../../utils/responsive';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface OTPInputProps {
  value: string;
  onChangeText: (text: string) => void;
  length?: number;
  size?: 'sm' | 'md' | 'lg';
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onComplete?: (code: string) => void;
  containerStyle?: any;
  inputStyle?: any;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  value,
  onChangeText,
  length = 4,
  size = 'md',
  error = false,
  disabled = false,
  autoFocus = false,
  onComplete,
  containerStyle,
  inputStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(''));
  const [hasPasted, setHasPasted] = useState(false);
  const inputRefs = useRef<(TextInput | null)[]>([]);
  const { responsiveSpacing, responsiveFontSize, isWeb, isTablet } = useResponsive();

  // Update internal state when value prop changes
  useEffect(() => {
    if (value && value.length === length) {
      const newOtp = value.split('').slice(0, length);
      setOtp(newOtp);
    }
  }, [value, length]);

  // Responsive sizing
  const getInputSize = () => {
    if (isTablet) {
      return 60;
    } else if (isWeb) {
      return 50;
    }
    return 48;
  };

  const inputSize = getInputSize();
  const inputGap = responsiveSpacing.sm;
  // Ensure container is properly centered by calculating exact width needed
  const containerWidth = length * inputSize + (length - 1) * inputGap;

  const handleOtpChange = (text: string, index: number) => {
    // A multi-character value here means the OS delivered a paste/autofill directly into this
    // box (native iOS/Android paste doesn't go through the web-only onPaste handler below) —
    // distribute it across the remaining boxes instead of truncating to a single character.
    if (text.length > 1) {
      const digitsOnly = text.replace(/\D/g, '');
      const newOtp = [...otp];
      let cursor = index;
      for (const digit of digitsOnly) {
        if (cursor >= length) break;
        newOtp[cursor] = digit;
        cursor++;
      }
      setOtp(newOtp);
      const otpString = newOtp.join('');
      onChangeText(otpString);
      const nextEmptyIndex = newOtp.findIndex(d => !d);
      const focusTarget = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
      inputRefs.current[focusTarget]?.focus?.();
      if (otpString.length === length && onComplete) {
        onComplete(otpString);
      }
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = text;
    setOtp(newOtp);

    // Update parent value
    const otpString = newOtp.join('');
    onChangeText(otpString);

    // Auto-focus next input
    if (text && index < length - 1) {
      const nextInput = inputRefs.current[index + 1];
      if (nextInput && typeof nextInput.focus === 'function') {
        nextInput.focus();
      }
    }

    // Check if OTP is complete
    if (otpString.length === length && onComplete) {
      onComplete(otpString);
    }
  };

  const handleKeyPress = (e: any, index: number) => {
    // Handle backspace navigation (works on both platforms)
    if (e.nativeEvent?.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input on backspace
      const prevInput = inputRefs.current[index - 1];
      if (prevInput && typeof prevInput.focus === 'function') {
        prevInput.focus();
      }
    }
  };

  const handleInputFocus = (index: number) => {
    // Select text when focusing (only on native platforms)
    if (otp[index] && Platform.OS !== 'web') {
      inputRefs.current[index]?.setSelection?.(0, otp[index].length);
    }
  };

  const clearOtp = () => {
    setOtp(new Array(length).fill(''));
    onChangeText('');
    // Focus first input (works on both platforms)
    const firstInput = inputRefs.current[0];
    if (firstInput && typeof firstInput.focus === 'function') {
      firstInput.focus();
    }
  };

  // Handle paste functionality
  const handlePaste = (text: string) => {
    if (hasPasted) return; // Only allow paste once
    
    const cleanText = text.replace(/\D/g, '').slice(0, length);
    if (cleanText.length === length) {
      const newOtp = cleanText.split('');
      setOtp(newOtp);
      onChangeText(cleanText);
      setHasPasted(true);
      
      if (onComplete) {
        onComplete(cleanText);
      }
    }
  };

  return (
    <View style={[styles.container, { width: containerWidth, gap: responsiveSpacing.sm }, containerStyle]}>
      {otp.map((digit, index) => (
        <TextInput
          key={index}
          ref={(ref) => (inputRefs.current[index] = ref)}
          style={[
            styles.otpInput,
            {
              width: inputSize,
              height: inputSize,
              fontSize: responsiveFontSize.lg,
              borderRadius: responsiveSpacing.md,
              textAlign: 'center',
              textAlignVertical: 'center',
            },
            error && styles.otpInputError,
            disabled && styles.otpInputDisabled,
            inputStyle,
          ]}
          value={digit}
          onChangeText={(text) => handleOtpChange(text, index)}
          onKeyPress={(e) => handleKeyPress(e, index)}
          onFocus={() => handleInputFocus(index)}
          onPaste={Platform.OS === 'web' ? (e) => handlePaste(e.nativeEvent.text) : undefined}
          keyboardType="number-pad"
          editable={!disabled}
          autoFocus={autoFocus && index === 0}
          selectTextOnFocus
          textAlign="center"
          placeholderTextColor={colors.textLight}
        />
      ))}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    width: '100%',
  },
  otpInput: {
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    fontWeight: typography.fontWeight.medium,
  },
  otpInputError: {
    borderColor: colors.error,
  },
  otpInputDisabled: {
    backgroundColor: colors.lightGray,
    opacity: 0.6,
  },
});
